import type { BrickCellKind } from "../data/ChapterData.js";
import { GOLD_BRICK, MYSTERY_BRICK, SHIELD_BRICK, ARMOR_BRICK, REGEN_BRICK } from "../data/balance.js";
import type { WorldBrickTextures } from "../data/worldData.js";
import { BRICK_SHATTER_VARIANTS } from "./brickShatterMeta.generated.js";

/**
 * 벽돌 외형(텍스처 키 + tint 모드) 결정 — 순수(Phaser 비의존).
 *
 * 멀티셀 블록 전환 후 모양 다양성은 블록 크기(칸 수)로 표현하므로 도형 텍스처는 통일한다:
 * normal/hard/boss = 단일 `brick` 텍스처 + 강도 무지개색(strength, 약함=빨강→강함=보라),
 * gold = 전용 `brick_gold` + 고정 금색(fixed). 색은 brickStrength(strength01=칸 수 정규화)에서 파생.
 * 텍스처 키는 여기(데이터 lookup)가 SSoT이며 테마 매니페스트(themes/imageKeys.ts가 spread)가
 * 이 키들의 도형 spec을 정의하고 BootScene이 generateTexture한다.
 */

/** normal/hard/boss 공통 base 도형 키. */
export const NORMAL_BASE_KEY = "brick";

/** gold 벽돌 전용 base 도형 키. */
export const GOLD_BASE_KEY = "brick_gold";

/** mystery(?) 벽돌 전용 base 도형 키 (황금상자 tint). */
export const MYSTERY_BASE_KEY = "brick_mystery";

/** mystery 보상 상자는 월드 적 콘셉트와 무관한 공통 PNG 아이콘을 사용한다. */
export const MYSTERY_CHEST_ICON_KEY = "icon_mystery_chest";

/** 모든 벽돌 base 텍스처 키 (BootScene generateTexture 대상의 SSoT). */
export const ALL_BRICK_BASE_KEYS = [NORMAL_BASE_KEY, GOLD_BASE_KEY, MYSTERY_BASE_KEY] as const;

/** base 텍스처 키 유니온 — 테마 매니페스트가 이 키 전부의 도형 spec을 빠짐없이 정의하도록 강제. */
export type BrickBaseKey = (typeof ALL_BRICK_BASE_KEYS)[number];

/** 룬 오버레이 — 전 종류 공통 (기존 PNG 재사용). */
export const BRICK_RUNE_OVERLAY = "brick_rune_base";

const DEFAULT_BRICK_TEXTURES: WorldBrickTextures = {
  visualFamily: "default",
  normalBase: NORMAL_BASE_KEY,
  goldBase: GOLD_BASE_KEY,
  mysteryBase: MYSTERY_CHEST_ICON_KEY,
  overlay: BRICK_RUNE_OVERLAY,
};

/** tint 방식 — strength=강도 무지개(약함=빨강→강함=보라, 남은 HP로 명도), fixed=고정색(gold). */
export type BrickTintMode = "strength" | "fixed";

export interface BrickTextureSpec {
  /** base 스프라이트 텍스처 키. */
  readonly base: string;
  /** 룬 오버레이 텍스처 키. */
  readonly overlay: string;
  /** base에도 tint를 곱할지 여부. 월드 normal PNG는 프리뷰 색을 보존한다. */
  readonly tintBase: boolean;
  /** overlay에도 base tint를 곱할지 여부. 월드 PNG 오버레이는 고유 색을 보존한다. */
  readonly tintOverlay: boolean;
  /** tint 방식. */
  readonly tintMode: BrickTintMode;
  /** tintMode가 fixed일 때 적용할 0xRRGGBB 색. */
  readonly fixedTint?: number;
}

/** "#rrggbb" → 0xRRGGBB 정수 (앞 # 허용, 24bit 클램프). 순수 — Phaser 비의존. */
export function hexToTint(hex: string): number {
  return parseInt(hex.replace(/^#/, ""), 16) & 0xffffff;
}

/**
 * 셀의 kind → 외형 스펙. gold=전용 base + 고정 금색, mystery=전용 base + 황금상자 tint, 그 외는 통일 base + 강도 무지개색.
 * mystery 런타임 표시는 Brick이 base를 숨기고 PNG 상자 + HP 숫자만 보여준다.
 */
export function pickBrickTexture(kind: BrickCellKind, worldTextures: WorldBrickTextures = DEFAULT_BRICK_TEXTURES): BrickTextureSpec {
  const tintBase = worldTextures === DEFAULT_BRICK_TEXTURES;
  const tintOverlay = worldTextures === DEFAULT_BRICK_TEXTURES;
  if (kind === "gold") {
    return {
      base: worldTextures.goldBase,
      overlay: worldTextures.overlay,
      tintBase,
      tintOverlay,
      tintMode: "fixed",
      fixedTint: hexToTint(GOLD_BRICK.tint),
    };
  }
  if (kind === "mystery") {
    return {
      base: worldTextures.mysteryBase,
      overlay: worldTextures.overlay,
      tintBase: false,
      tintOverlay: false,
      tintMode: "fixed",
      fixedTint: hexToTint(MYSTERY_BRICK.tint),
    };
  }
  // 특수 벽돌 — 전용 PNG 없이 일반 base 도형 + 고정 색만으로 구분(청록/회색/민트). shield는 방어막 소진 시
  // Brick.applyTint가 strength 무지개로 되돌린다(여기 fixed는 방어막 살아있는 동안의 색).
  if (kind === "shield") {
    return { base: worldTextures.normalBase, overlay: worldTextures.overlay, tintBase: true, tintOverlay, tintMode: "fixed", fixedTint: hexToTint(SHIELD_BRICK.tint) };
  }
  if (kind === "armor") {
    return { base: worldTextures.normalBase, overlay: worldTextures.overlay, tintBase: true, tintOverlay, tintMode: "fixed", fixedTint: hexToTint(ARMOR_BRICK.tint) };
  }
  if (kind === "regen") {
    return { base: worldTextures.normalBase, overlay: worldTextures.overlay, tintBase: true, tintOverlay, tintMode: "fixed", fixedTint: hexToTint(REGEN_BRICK.tint) };
  }
  return { base: worldTextures.normalBase, overlay: worldTextures.overlay, tintBase, tintOverlay, tintMode: "strength" };
}

/** gold 셀이면 보상 배수, 그 외는 1. (buildStageGrid의 rewardGold 합성용 순수 helper.) */
export function goldKindMult(kind: BrickCellKind, multiplier: number): number {
  return kind === "gold" ? multiplier : 1;
}

/** 특수 벽돌 kind별 HP 배율(일반=1). spawn 단계 hp 합성용 순수 helper. */
export function specialBrickHpFactor(kind: BrickCellKind): number {
  if (kind === "shield") return SHIELD_BRICK.hpFactor;
  if (kind === "armor") return ARMOR_BRICK.hpFactor;
  if (kind === "regen") return REGEN_BRICK.hpFactor;
  return 1;
}

/** 특수 벽돌 kind별 보상 골드 배수(일반=1). spawn 단계 rewardGold 합성용 순수 helper. */
export function specialBrickGoldMult(kind: BrickCellKind): number {
  if (kind === "shield") return SHIELD_BRICK.goldMultiplier;
  if (kind === "armor") return ARMOR_BRICK.goldMultiplier;
  if (kind === "regen") return REGEN_BRICK.goldMultiplier;
  return 1;
}

/**
 * 균열 variant + 단계 → 오버레이 텍스처 키 (stage 0 = 균열 없음 → 빈 키) — 순수.
 * variant는 brickShatter 분할 기하와 짝이라 파괴 시 표시되던 균열 패턴 그대로 조각난다.
 * 범위 밖 variant는 클램프 — variant 수의 SSoT는 생성기 emit 메타(brickShatterMeta.generated)다.
 */
export function crackTextureFor(variant: number, stage: 0 | 1 | 2 | 3 | 4): string {
  const idx = Math.min(BRICK_SHATTER_VARIANTS.length - 1, Math.max(0, Math.floor(variant)));
  return BRICK_SHATTER_VARIANTS[idx]!.crackStageTextures[stage];
}

/**
 * 남은 HP 비율(0~1) → 균열 단계 (0=없음, 1~4=손상 누적) — 순수.
 * "HP 낮은 블록은 균열" 가독 지시: tint 명도 저하만으로는 손상도가 안 읽혀 균열 라인을 병행한다.
 */
export function crackStageFor(hpRatio: number): 0 | 1 | 2 | 3 | 4 {
  if (hpRatio <= 0.2) return 4;
  if (hpRatio <= 0.4) return 3;
  if (hpRatio <= 0.6) return 2;
  if (hpRatio <= 0.8) return 1;
  return 0;
}
